import React, { useEffect, useState, useContext } from 'react';
import { PlusCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Modal, SideBar, Table } from '../../../../imports';
import AlertContext from '../../../GlobalComponents/Alert/contexts/alertContext';
import { useLoader } from '../../../../context/LoaderContext.';
import {
    ObtenerFormularios,
    ObtenerFormularioPorId,
    AgregarFormulario,
    EditarFormulario,
    CambiarEstadoFormulario,
} from '../../../../servicios/ServicioFormulario';

import {
    ObtenerPreguntasPorFormulario,
    AgregarPregunta,
    EditarPregunta,
    EliminarPregunta,
} from '../../../../servicios/ServicioPreguntaFormulario';
import { GuardarRespuesta } from '../../../../servicios/ServicioEvaluacion';

const debugLog = (label, value) => {
    console.log(`[DEBUG] ${label}:`, value);
};

const safeHandleSubmit = (fn) => (e) => {
    e?.preventDefault?.();
    if (typeof fn === 'function') return fn(e);
};

const tipos = [
    { value: 'likert', label: 'Escala (1-5)' },
    { value: 'sino', label: 'Sí/No' },
    { value: 'abierta', label: 'Abierta' },
];

const nuevaPregunta = (orden) => ({
    IdPregunta: null,
    Texto: '',
    Tipo: 'likert',
    Orden: orden,
    Requerida: true,
    Activa: true,
});


const FormularioModalEditor = React.forwardRef(({ idFormulario = 0, onClose, onSuccess }, ref) => {
    const [formulario, setFormulario] = useState({
        IdFormulario: 0,
        Nombre: '',
        Descripcion: '',
        Activo: true,
    });
    const [preguntas, setPreguntas] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [cargando, setCargando] = useState(false);

    const cargar = async () => {
        if (!idFormulario) return;
        setCargando(true);
        try {
            debugLog('Cargando formulario con ID', idFormulario);
            const f = await ObtenerFormularioPorId(idFormulario);
            debugLog('Respuesta ObtenerFormularioPorId', f);

            if (f) {
                setFormulario({
                    IdFormulario: f.idFormulario ?? f.IdFormulario,
                    Nombre: f.nombre ?? f.Nombre,
                    Descripcion: f.descripcion ?? f.Descripcion,
                    Activo: f.activo ?? f.Activo,
                });
                const ps = await ObtenerPreguntasPorFormulario(idFormulario);
                debugLog('Preguntas cargadas', ps);

                setPreguntas(
                    Array.isArray(ps)
                        ? ps.map((p) => ({
                            IdPregunta: p.idPregunta ?? p.IdPregunta,
                            IdFormulario: p.idFormulario ?? p.IdFormulario,
                            Texto: p.texto ?? p.Texto,
                            Tipo: p.tipo ?? p.Tipo,
                            Orden: p.orden ?? p.Orden,
                            Requerida: p.requerida ?? p.Requerida,
                            Activa: p.activa ?? p.Activa,
                        }))
                        : []
                );
            }
        } catch (err) {
            console.error('[ERROR cargar()]', err);
            alert('Error cargando formulario: ' + err.message);
        }
        setCargando(false);
    };

    useEffect(() => {
        cargar();
        
    }, [idFormulario]);

    const addPregunta = () => {
        const nextOrden = (preguntas[preguntas.length - 1]?.Orden ?? preguntas.length) + 1;
        setPreguntas([...preguntas, nuevaPregunta(nextOrden)]);
    };

    const updatePregunta = (idx, key, value) => {
        const copy = [...preguntas];
        copy[idx] = { ...copy[idx], [key]: value };
        setPreguntas(copy);
    };

    const removePregunta = async (idx) => {
        const p = preguntas[idx];

        try {
            if (p.IdPregunta) {
                debugLog('Eliminando pregunta', p.IdPregunta);

                const res = await EliminarPregunta(p.IdPregunta);

                
                if (res?.indicador !== 0) {
                    alert("❌ No se puede eliminar esta pregunta porque ya tiene respuestas registradas.");
                    return;
                }
            }
        } catch (err) {
            console.error('[ERROR eliminar pregunta]', err);

            
            alert("❌ No se puede eliminar esta pregunta porque ya tiene respuestas registradas.");

            return; 
        }

        
        const copy = [...preguntas];
        copy.splice(idx, 1);
        setPreguntas(copy.map((x, i) => ({ ...x, Orden: i + 1 })));
    };



    const guardar = async () => {
        if (!formulario.Nombre?.trim()) {
            alert('El nombre es requerido');
            return;
        }

        setGuardando(true);
        let idForm = formulario.IdFormulario;

        try {
            if (!idForm) {
                debugLog('Creando formulario', formulario);
                const res = await AgregarFormulario({
                    Nombre: formulario.Nombre,
                    Descripcion: formulario.Descripcion,
                    Activo: true,
                });
                debugLog('Respuesta AgregarFormulario', res);

                const indicador = res?.indicador ?? 0;
                let mensaje = res?.mensaje ?? '';
                let idGenerado = 0;

                idGenerado = parseInt(mensaje) || 0;
                idForm = idGenerado;
                debugLog('ID tomado del mensaje del SP', idForm);

                if (indicador !== 0 || !idForm) {
                    alert(`No se pudo crear el formulario. Mensaje: ${mensaje}`);
                    setGuardando(false);
                    return;
                }
            } else {
                
                debugLog('Editando formulario', formulario);
                const res = await EditarFormulario({
                    IdFormulario: idForm,
                    Nombre: formulario.Nombre,
                    Descripcion: formulario.Descripcion,
                    Activo: formulario.Activo,
                });
                debugLog('Respuesta EditarFormulario', res);

                if ((res?.indicador ?? 0) !== 0) {
                    alert(res?.mensaje || 'Error al editar formulario');
                    setGuardando(false);
                    return;
                }
            }

            
            for (const p of preguntas) {
                const payload = {
                    IdPregunta: p.IdPregunta,
                    IdFormulario: idForm,
                    Texto: p.Texto,
                    Tipo: p.Tipo,
                    Orden: p.Orden,
                    Requerida: p.Requerida,
                    Activa: p.Activa,
                };
                debugLog('Guardando pregunta', payload);

                try {
                    let r;
                    if (!p.IdPregunta) r = await AgregarPregunta(payload);
                    else r = await EditarPregunta(payload);
                    debugLog('Respuesta de pregunta', r);
                } catch (err) {
                    console.error('[ERROR guardando pregunta]', err);
                }
            }

            debugLog('Formulario y preguntas guardados correctamente', { idForm });
            setGuardando(false);
            onSuccess?.();
            onClose?.();
        } catch (err) {
            console.error('[ERROR guardar()]', err);
            alert('Error guardando formulario: ' + err.message);
            setGuardando(false);
        }
    };

    
    React.useImperativeHandle(ref, () => ({
        guardar,
    }));

    
    return (
        <div className="p-4 text-white bg-[#1b1b1b] rounded-lg">
            {cargando ? (
                <p>Cargando...</p>
            ) : (
                <>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-200">Nombre</label>
                            <input
                                className="w-full border border-gray-500 bg-transparent text-white p-2 rounded"
                                value={formulario.Nombre}
                                onChange={(e) => setFormulario({ ...formulario, Nombre: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-200">Descripción</label>
                            <input
                                className="w-full border border-gray-500 bg-transparent text-white p-2 rounded"
                                value={formulario.Descripcion}
                                onChange={(e) => setFormulario({ ...formulario, Descripcion: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-semibold text-white">Preguntas</h2>
                        <button onClick={addPregunta} className="bg-blue-600 text-white px-3 py-1 rounded">
                            Agregar pregunta
                        </button>
                    </div>

                    {preguntas.map((p, idx) => (
                        <div key={idx} className="border border-gray-600 rounded p-3 mb-2">
                            <div className="grid md:grid-cols-12 gap-3 items-center">
                                <input
                                    type="number"
                                    className="md:col-span-1 border border-gray-600 bg-transparent text-white p-1 rounded"
                                    value={p.Orden}
                                    min={1}
                                    onChange={(e) => updatePregunta(idx, 'Orden', Number(e.target.value))}
                                />
                                <input
                                    className="md:col-span-6 border border-gray-600 bg-transparent text-white p-1 rounded"
                                    placeholder="Texto de la pregunta"
                                    value={p.Texto}
                                    onChange={(e) => updatePregunta(idx, 'Texto', e.target.value)}
                                />
                                <select
                                    className="md:col-span-2 border border-gray-600 bg-transparent text-white p-1 rounded"
                                    value={p.Tipo}
                                    onChange={(e) => updatePregunta(idx, 'Tipo', e.target.value)}
                                >
                                    {tipos.map((t) => (
                                        <option key={t.value} value={t.value} className="text-black">
                                            {t.label}
                                        </option>
                                    ))}
                                </select>
                                <label className="md:col-span-1 flex items-center gap-2 text-sm text-gray-200">
                                    <input
                                        type="checkbox"
                                        checked={!!p.Requerida}
                                        onChange={(e) => updatePregunta(idx, 'Requerida', e.target.checked)}
                                    />
                                    Req.
                                </label>
                                <label className="md:col-span-1 flex items-center gap-2 text-sm text-gray-200">
                                    <input
                                        type="checkbox"
                                        checked={!!p.Activa}
                                        onChange={(e) => updatePregunta(idx, 'Activa', e.target.checked)}
                                    />
                                    Activa
                                </label>
                                <button
                                    className="md:col-span-1 bg-red-600 text-white rounded px-2 py-1"
                                    onClick={() => removePregunta(idx)}
                                >
                                    X
                                </button>
                            </div>
                        </div>
                    ))}

                    
                </>
            )}
        </div>
    );
});


export default function FormularioCatalog() {
    const [lista, setLista] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showStateModal, setShowStateModal] = useState(false);
    const [idEdit, setIdEdit] = useState(null);
    const [idEstado, setIdEstado] = useState(null);
    const [estadoActual, setEstadoActual] = useState(null);

    const alertCtx = useContext(AlertContext);
    const { showLoader, hideLoader } = useLoader();

    const addRef = React.useRef(null);
    const editRef = React.useRef(null);

    const cargar = async () => {
        try {
            showLoader();
            const data = await ObtenerFormularios();
            hideLoader();
            debugLog('Respuesta ObtenerFormularios', data);
            setLista(data || []);
        } catch (err) {
            hideLoader();
            console.error('[ERROR cargar() lista]', err);
            alert('Error cargando formularios: ' + err.message);
        }
    };

    useEffect(() => {
        cargar();
        
    }, []);

    const abrirAgregar = () => setShowAddModal(true);
    const abrirEditar = (row) => {
        setIdEdit(row?.IdFormulario ?? row?.idFormulario);
        setShowEditModal(true);
    };
    const abrirEstado = (row) => {
        setIdEstado(row?.IdFormulario ?? row?.idFormulario);
        setEstadoActual(row?.Activo ?? row?.activo);
        setShowStateModal(true);
    };

    const cambiarEstado = async () => {
        try {
            showLoader();
            const r = await CambiarEstadoFormulario(idEstado, !estadoActual);
            hideLoader();
            debugLog('Respuesta CambiarEstadoFormulario', r);

            if ((r?.indicador ?? 0) === 0) {
                alertCtx.success(r.mensaje);
                setShowStateModal(false);
                await cargar();
            } else {
                alertCtx.error(r?.mensaje || 'Error al cambiar estado');
            }
        } catch (err) {
            hideLoader();
            console.error('[ERROR cambiarEstado()]', err);
            alert('Error al cambiar estado: ' + err.message);
        }
    };

    const columns = [
        {
            name: 'ID',
            selector: (row) => row?.IdFormulario ?? row?.idFormulario,
            width: '90px',
            cell: (row) => (
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg font-semibold text-sm shadow-sm">
                    {row?.IdFormulario ?? row?.idFormulario}
                </span>
            ),
        },
        {
            name: 'Nombre',
            selector: (row) => row?.Nombre ?? row?.nombre,
            cell: (row) => (
                <span className="font-medium text-gray-800">
                    {row?.Nombre ?? row?.nombre}
                </span>
            ),
        },
        {
            name: 'Descripción',
            selector: (row) => row?.Descripcion ?? row?.descripcion,
            cell: (row) => (
                <span className="text-gray-600">
                    {row?.Descripcion ?? row?.descripcion}
                </span>
            ),
        },
        {
            name: 'Activo',
            width: '130px',
            selector: (row) => (row?.Activo ?? row?.activo ? 'Sí' : 'No'),
            cell: (row) => (
                row?.Activo ?? row?.activo ? (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 shadow-sm">
                        Activo
                    </span>
                ) : (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 shadow-sm">
                        Inactivo
                    </span>
                )
            ),
        },
        {
            name: 'Creado',
            selector: (row) => row?.Creado ?? row?.creado ?? row?.fechaCreacion,
            width: '220px',
            cell: (row) => (
                <span className="text-gray-600 text-sm">
                    {row?.Creado ?? row?.creado ?? row?.fechaCreacion}
                </span>
            ),
        },
        {
            name: 'Acciones',
            width: '160px',
            ignoreRowClick: true,
            allowOverflow: true,
            cell: (row) => (
                <div className="flex items-center gap-2">

                    
                    <button
                        onClick={() => abrirEditar(row)}
                        className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition shadow-sm"
                        title="Editar"
                    >
                        
                        <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                    </button>

                    
                    {row?.Activo ?? row?.activo ? (
                        <button
                            onClick={() => abrirEstado(row)}
                            className="p-2 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 transition shadow-sm"
                            title="Desactivar"
                        >
                            
                            <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="9" />
                                <line x1="15" y1="9" x2="9" y2="15" />
                                <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                        </button>
                    ) : (
                        <button
                            onClick={() => abrirEstado(row)}
                            className="p-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition shadow-sm"
                            title="Activar"
                        >
                            
                            <svg width="20" height="20" viewBox="0 0 24 24" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M9 12l2 2 4-4" />
                            </svg>
                        </button>
                    )}

                </div>
            ),
        },
    ];



    const ConfirmarCambioEstado = () => (
        <form className="space-y-2 md:space-y-4 text-white bg-[#1b1b1b] p-4 rounded-lg">
            <div className="w-96 container mx-auto items-center justify-center">
                <div className="flex flex-col">
                    <div className="flex text-base m-2 justify-center mx-auto font-bold leading-tight tracking-tight text-yellow-400">
                        <ExclamationTriangleIcon className="w-8 h-8 ml-2" />
                    </div>
                    <div>
                        <h1 className="flex text-base justify-center mx-auto font-bold leading-tight tracking-tight text-white">
                            Advertencia
                        </h1>
                    </div>
                    <div>
                        <label className="flex text-base justify-center mx-auto m-2 py-2 text-sm font-medium text-gray-300">
                            ¿Está seguro que desea cambiar el estado de este formulario?
                        </label>
                    </div>
                </div>
            </div>
        </form>
    );

    return (
        <div className="flex flex-row">
            <div className="md:basis-1/4 xl:basis-1/5">
                <SideBar />
            </div>

            <div className="md:basis-3/4 xl:basis-11/12">
                <div className="bg-white rounded-lg shadow-lg shadow-slate-400 px-2 py-2 mt-2 m-2 mr-8">
                    <div className="px-2">
                        <div className="absolute z-20 ml-4 mt-1">
                            <h1 className="left-0 text-xl font-bold text-black-RS mb-1">Catálogo Formularios</h1>
                            <div className="items-center sm:flex">
                                <button
                                    type="button"
                                    onClick={abrirAgregar}
                                    className="bg-greenButton text-white text-sm py-2 px-4 rounded inline-flex items-center hover:bg-primary-700"
                                >
                                    <span>Agregar</span>
                                    <PlusCircleIcon className="w-8 h-8 ml-2" />
                                </button>
                            </div>
                        </div>

                        <div className="static px-2 mt-2 m-2">
                            <Table columns={columns} title="formularios" filteredItems="" data={lista} filterBy="nombre" />
                        </div>
                    </div>

                    {/* Modales */}
                    <Modal
                        insideContainer={{
                            className: 'w-full text-white',
                            style: { maxWidth: '1000px', padding: '24px' },
                        }}
                        isVisible={showAddModal}
                        onClose={() => setShowAddModal(false)}
                        handleSubmit={safeHandleSubmit}
                        onSaveOptions={() => addRef.current?.guardar()}
                        showCloseIcon={false}
                    >
                        <FormularioModalEditor ref={addRef} idFormulario={0} onClose={() => setShowAddModal(false)} onSuccess={cargar} />
                    </Modal>

                    <Modal
                        insideContainer={{
                            className: 'w-full text-white',
                            style: { maxWidth: '1000px', padding: '24px' },
                        }}
                        isVisible={showEditModal}
                        onClose={() => setShowEditModal(false)}
                        handleSubmit={safeHandleSubmit}
                        onSaveOptions={() => editRef.current?.guardar()}
                        showCloseIcon={false}
                    >
                        <FormularioModalEditor ref={editRef} idFormulario={idEdit} onClose={() => setShowEditModal(false)} onSuccess={cargar} />
                    </Modal>

                    <Modal
                        insideContainer={{
                            className: 'text-white',
                        }}
                        isVisible={showStateModal}
                        onClose={() => setShowStateModal(false)}
                        handleSubmit={safeHandleSubmit}
                        onSaveOptions={cambiarEstado}
                        showCloseIcon={false}
                    >
                        <ConfirmarCambioEstado />
                    </Modal>
                </div>
            </div>
        </div>
    );
}
